/**
 * statusHistory services (VE-26730): gate resolution, the serialized
 * best-effort writer, and the two-tier reads for the
 * `redact-tdo-status-history` SDO.
 *
 * Failure contract (BR-2 / SECURITY-15): a history write NEVER rejects into its
 * caller — the status change it trails must not be blocked, delayed, or rolled
 * back by history bookkeeping. Lost rows are reported (console.error + a
 * LOG_AUDIT_EVENT breadcrumb, NFR N-10) and swallowed.
 *
 * `softDeleteStatusHistoryRows` is the one deliberate exception, because it
 * trails nothing: it is a button the user pressed, with no status change riding
 * on it, and its caller has to know whether the clear finished before deciding
 * to write the tombstone. Swallowing there would let a half-finished clear be
 * recorded as a completed one.
 *
 * Gate contract (FR-7 / BR-1): "not provisioned" is a NORMAL state, not an
 * error. The registry id comes from `resolveTdoStatusUpdatesRegistryId` — a
 * built-in default that config may override or blank out (see ../registryId). The registry lookup is done here rather than through notification's
 * getLatestSchemaId precisely because that helper dispatches SCHEMA_ID_NOT_FOUND,
 * which a global watcher turns into a user-facing error snackbar — an
 * unprovisioned environment would toast on every editor open. This module
 * self-disables silently instead.
 *
 * Silently, but NOT indiscriminately: only an *authoritative* answer settles the
 * gate to 'disabled' (no registry id in config, or the registry query succeeded
 * and reported no published schema). A failed request is transient and leaves
 * the gate where it was, to be retried on a later demand behind a cooldown.
 * Collapsing the two would mean one 502 or expired-token blip silently stops
 * every history write for the rest of the session — and these rows are the
 * record customers read as a compliance trail, so losing them quietly is the
 * worst available failure mode.
 */
import { modules } from '@veritone/glc-redux';
import { v4 as uuid } from 'uuid';

import callGraphQLApi, {
  GQLApiDispatch,
  GQLApiGetState,
} from '@helpers/callGraphQLApi';

import { LOG_AUDIT_EVENT } from '@common-modules/mediaDetails/actions/audit-logs';

import {
  FETCH_LATEST_STATUS_ROW_FAILURE,
  FETCH_LATEST_STATUS_ROW_SUCCESS,
  FETCH_STATUS_HISTORY,
  FETCH_STATUS_HISTORY_FAILURE,
  RESET_STATUS_HISTORY,
  FETCH_STATUS_HISTORY_SUCCESS,
  STATUS_HISTORY_GATE_RESOLVED,
} from '../actions';
import {
  RecordStatusChangeInput,
  STATUS_HISTORY_PAGE_SIZE,
  StatusHistoryRecord,
  mapSdoRecordToStatusHistoryRecord,
  mapStatusChangeToSdoData,
} from '../models';
import { resolveTdoStatusUpdatesRegistryId } from '../registryId';
import { selectStatusHistoryGate } from '../selectors';
import {
  FetchStatusHistoryResponse,
  ResolveSchemaIdResponse,
  WriteStatusHistoryRowResponse,
  buildDeleteRowsQuery,
  buildSoftDeleteRowsQuery,
  fetchStatusHistoryQuery,
  resolveSchemaIdQuery,
  writeStatusHistoryRowQuery,
} from './queries';

const {
  config: { getConfig },
  user: userModule,
} = modules;

const NOOP = 'noop';

const getRegistryId = (getState: GQLApiGetState): string | undefined => {
  const config = getConfig(getState()) as
    | { tdoStatusHistoryRegistryId?: string; apiRoot?: string }
    | undefined;
  return resolveTdoStatusUpdatesRegistryId(config?.tdoStatusHistoryRegistryId);
};

const getSchemaCacheKey = (getState: GQLApiGetState): string | undefined => {
  const config = getConfig(getState()) as
    | { tdoStatusHistoryRegistryId?: string; apiRoot?: string }
    | undefined;
  const registryId = resolveTdoStatusUpdatesRegistryId(
    config?.tdoStatusHistoryRegistryId
  );
  if (!registryId) {
    return undefined;
  }
  return `${config?.apiRoot || ''}-${registryId}`;
};

/**
 * Resolved schema ids, keyed by `${apiRoot}-${registryId}`.
 *
 * Module scope, NOT sessionStorage (VE-28974): sessionStorage survives a page
 * reload, so a republished schema left the tab holding the superseded id until
 * it was *closed* — reloading did nothing. The stale-schema retry below still
 * recovers a write, but only in-memory caching makes a reload work too.
 *
 * As a side benefit this removes the storage-blocked-context handling that
 * sessionStorage needed (it throws outright in Safari private mode and in some
 * third-party iframes).
 */
const schemaIdCache = new Map<string, string>();

const readCachedSchemaId = (
  cacheKey: string | undefined
): string | undefined => (cacheKey ? schemaIdCache.get(cacheKey) : undefined);

const writeCachedSchemaId = (
  cacheKey: string | undefined,
  schemaId: string
) => {
  if (cacheKey) {
    schemaIdCache.set(cacheKey, schemaId);
  }
};

const clearCachedSchemaId = (cacheKey: string | undefined) => {
  if (cacheKey) {
    schemaIdCache.delete(cacheKey);
  }
};

/**
 * Whether a failed write looks like "the cached schema id is stale" (the schema
 * was republished) rather than a transport failure. Only the former justifies
 * evicting the cached id and re-resolving: doing that for a network error throws
 * away a good schema id mid-outage, and the re-resolve then fails too.
 */
const isStaleSchemaError = (err: unknown): boolean => {
  const parts: string[] = [];
  if (err instanceof Error && err.message) {
    parts.push(err.message);
  }
  const errors = (err as { readonly errors?: unknown } | null | undefined)
    ?.errors;
  if (Array.isArray(errors)) {
    for (const entry of errors as readonly {
      readonly name?: unknown;
      readonly message?: unknown;
    }[]) {
      if (typeof entry?.name === 'string') {
        parts.push(entry.name);
      }
      if (typeof entry?.message === 'string') {
        parts.push(entry.message);
      }
    }
  }
  const haystack = parts.join(' ').toLowerCase();
  return haystack.includes('schema') || haystack.includes('validation');
};

/**
 * Fixed-vocabulary error classification for logs. NEVER log the raw error from
 * this module: server-authored messages can echo submitted values (the note is
 * user-authored case text), and Sentry's default console-breadcrumb integration
 * ships console.error args verbatim off-product (SECURITY-03).
 */
const classifyErrorForLog = (err: unknown): string => {
  if (isStaleSchemaError(err)) {
    return 'schema_or_validation';
  }
  if (err instanceof Error && err.name && err.name !== 'Error') {
    return err.name;
  }
  return 'request_failed';
};

/**
 * Result of one registry lookup. The three cases are deliberately distinct:
 * only `absent` is an authoritative "this environment is not provisioned" and
 * may settle the gate to 'disabled'. `error` says nothing about provisioning.
 */
type SchemaLookup =
  | { readonly outcome: 'published'; readonly schemaId: string }
  | { readonly outcome: 'absent' }
  | { readonly outcome: 'error' };

/**
 * Looks up the registry's published schema id, classifying the outcome.
 *
 * Silent by design — no action is dispatched that any other module watches —
 * but it distinguishes "the registry has no published schema" (a fact about the
 * environment) from "the request failed" (a fact about the network). Note that
 * callGraphQLApi resolves `undefined` for a `not_found` GraphQL error and throws
 * for everything else, so an unprovisioned registry lands in `absent` rather
 * than `error`.
 */
const fetchPublishedSchemaId = async (
  dispatch: GQLApiDispatch,
  getState: GQLApiGetState,
  registryId: string
): Promise<SchemaLookup> => {
  try {
    const response = await callGraphQLApi<ResolveSchemaIdResponse>({
      actionTypes: [NOOP, NOOP, NOOP],
      query: resolveSchemaIdQuery,
      variables: { dataRegistryId: registryId },
      dispatch,
      getState,
      bailout: undefined,
    });
    const schemaId = response?.dataRegistry?.publishedSchema?.id;
    return typeof schemaId === 'string' && schemaId
      ? { outcome: 'published', schemaId }
      : { outcome: 'absent' };
  } catch (err) {
    // Transient by classification — the gate stays unresolved and a later
    // demand retries. Loud on purpose: rows demanded during the cooldown are
    // NOT recorded (see writeStatusHistoryRow), and a silent gap in the record
    // customers read as a compliance trail is the worst failure mode here.
    console.error('statusHistory: schema lookup failed; gate unresolved', {
      kind: classifyErrorForLog(err),
    });
    return { outcome: 'error' };
  }
};

/**
 * After a transient lookup failure, wait this long before spending another
 * request on it. Bounds retry volume during an outage WITHOUT ever latching the
 * feature off — the next demand after the cooldown tries again.
 */
const GATE_RETRY_COOLDOWN_MS = 30_000;
let gateRetryBlockedUntil = 0;
/** Dedupes concurrent first-demand lookups (a multi-file upload seeds N rows). */
let inFlightGateLookup: Promise<string | undefined> | undefined;

/** Test seam: the module-level lookup state is per-session, not per-store. */
export const resetStatusHistoryGateBackoff = () => {
  gateRetryBlockedUntil = 0;
  inFlightGateLookup = undefined;
};

/**
 * Test seam for the resolved-schema-id cache. Separate from the backoff reset
 * above rather than folded into it: a reset named for the backoff that also
 * dropped cached schema ids would be exactly the sort of name that hides what it
 * does. Production code clears by key via `clearCachedSchemaId`.
 */
export const resetStatusHistorySchemaCache = () => {
  schemaIdCache.clear();
};

/**
 * Resolves the published schema id and settles the gate (FR-7 / BR-1).
 *
 * Short-circuits, cheapest first:
 * - no registry id in config → 'disabled' (authoritative), no network call
 * - already settled 'disabled' → return (an unprovisioned environment must not
 *   re-issue the lookup on every file open)
 * - schema id already resolved this session → reuse it
 * - a lookup is already in flight → await that one
 * - a recent lookup failed transiently → stay 'unknown', retry after the cooldown
 * Only a cache miss on a not-yet-settled gate outside the cooldown hits the API.
 */
export const resolveStatusHistoryGate =
  () =>
  async (
    dispatch: GQLApiDispatch,
    getState: GQLApiGetState
  ): Promise<string | undefined> => {
    const registryId = getRegistryId(getState);
    if (!registryId) {
      // Authoritative: the feature is not provisioned in this environment.
      // Guarded so an unprovisioned deployment doesn't push a no-op action
      // through the middleware chain on every open and every status change.
      if (selectStatusHistoryGate(getState()) !== 'disabled') {
        dispatch(STATUS_HISTORY_GATE_RESOLVED({ enabled: false }));
      }
      return undefined;
    }
    if (selectStatusHistoryGate(getState()) === 'disabled') {
      return undefined;
    }
    const cacheKey = getSchemaCacheKey(getState);
    const cached = readCachedSchemaId(cacheKey);
    if (cached) {
      // Re-announce only on an actual change, so a cache hit on every read
      // doesn't push a no-op action through the middleware chain.
      if (selectStatusHistoryGate(getState()) !== 'enabled') {
        dispatch(
          STATUS_HISTORY_GATE_RESOLVED({ enabled: true, schemaId: cached })
        );
      }
      return cached;
    }
    if (inFlightGateLookup) {
      return await inFlightGateLookup;
    }
    if (Date.now() < gateRetryBlockedUntil) {
      return undefined;
    }
    const lookup = (async (): Promise<string | undefined> => {
      const result = await fetchPublishedSchemaId(
        dispatch,
        getState,
        registryId
      );
      if (result.outcome === 'published') {
        writeCachedSchemaId(cacheKey, result.schemaId);
        gateRetryBlockedUntil = 0;
        dispatch(
          STATUS_HISTORY_GATE_RESOLVED({
            enabled: true,
            schemaId: result.schemaId,
          })
        );
        return result.schemaId;
      }
      if (result.outcome === 'absent') {
        // Authoritative: the registry exists but publishes no schema.
        gateRetryBlockedUntil = 0;
        dispatch(STATUS_HISTORY_GATE_RESOLVED({ enabled: false }));
        return undefined;
      }
      // Transient. Deliberately dispatch NOTHING: the gate stays 'unknown' (or
      // stays 'enabled' if it had already resolved), so the UI keeps offering
      // the note prompt and the next demand retries. Disabling here would let
      // one network blip silently drop every later history row.
      gateRetryBlockedUntil = Date.now() + GATE_RETRY_COOLDOWN_MS;
      return undefined;
    })();
    inFlightGateLookup = lookup;
    try {
      return await lookup;
    } finally {
      inFlightGateLookup = undefined;
    }
  };

/**
 * One chain for every history write (pattern: redactionStatuses VE-24546) so a
 * bulk status change writes its N rows in order instead of racing them, with a
 * bound so one stalled request can't block the queue for the session.
 */
let historyPersistChain: Promise<unknown> = Promise.resolve();
const HISTORY_PERSIST_QUEUE_TIMEOUT_MS = 30_000;

const enqueueHistoryPersist = async <T>(run: () => Promise<T>): Promise<T> => {
  const previous = historyPersistChain;
  const gate = new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, HISTORY_PERSIST_QUEUE_TIMEOUT_MS);
    void previous
      .catch(() => undefined)
      .then(() => {
        clearTimeout(timer);
        resolve();
        return undefined;
      });
  });
  const next = gate.then(run);
  historyPersistChain = next.catch(() => undefined);
  return await next;
};

/**
 * NFR N-10: every lost history row is reported twice — console.error (local
 * diagnosis) and a LOG_AUDIT_EVENT breadcrumb into the queryable `redact`
 * audit bucket (production investigability; the watcher is registered
 * globally via initMediaDetails, so folder-view and upload losses are heard
 * too). The tdoId rides in the message because sendEvent's own payload tdoId
 * is the ACTIVE editor file, which is absent in the folder view. Fixed
 * vocabulary + tdoId/statusKey only — never note content or server messages.
 */
const reportRowLoss = (
  dispatch: GQLApiDispatch,
  input: RecordStatusChangeInput,
  kind: string
) => {
  console.error('statusHistory: row NOT recorded', {
    tdoId: input.tdoId,
    statusKey: input.statusKey,
    kind,
  });
  try {
    dispatch(
      LOG_AUDIT_EVENT(
        `Redaction status history write failed (${kind}) for TDO ${input.tdoId}`
      )
    );
  } catch {
    // The breadcrumb is best-effort bookkeeping about bookkeeping — it must
    // never block or reject into the caller (BR-2).
  }
};

/** A server-assigned SDO id: the only shape allowed into a mutation document. */
const SDO_ID_PATTERN = /^[0-9a-f-]{36}$/i;

/** Rows per bulk request; keeps one mutation document a sane size. */
const BULK_ROW_CHUNK = 25;

/**
 * Every row for a TDO, paged to the end, as its RAW SDO data.
 *
 * Raw, not mapped: the soft-delete rewrites these as an upsert, and rebuilding
 * from a mapped row would silently drop any field this client version does not
 * know about — a row written by a newer writer would come back stripped.
 *
 * Bounded so a server that keeps reporting a larger count than it returns
 * cannot spin here forever.
 */
const collectRowsForTdo = async (
  tdoId: string,
  schemaId: string,
  dispatch: GQLApiDispatch,
  getState: GQLApiGetState
): Promise<{ id: string; data: Record<string, unknown> }[]> => {
  const rows: { id: string; data: Record<string, unknown> }[] = [];
  let offset = 0;
  for (let page = 0; page < 200; page += 1) {
    const response = await callGraphQLApi<FetchStatusHistoryResponse>({
      actionTypes: [NOOP, NOOP, NOOP],
      query: fetchStatusHistoryQuery,
      variables: { schemaId, tdoId, limit: STATUS_HISTORY_PAGE_SIZE, offset },
      dispatch,
      getState,
      bailout: undefined,
    });
    const records = response?.structuredDataObjects?.records ?? [];
    if (records.length === 0) {
      break;
    }
    records.forEach((record) => {
      const data =
        record.data && typeof record.data === 'object' ? record.data : null;
      // Ids are interpolated into the batch mutation documents rather than
      // passed as variables (the alias count varies per call), so anything that
      // is not a plain uuid is dropped rather than trusted. These are
      // server-assigned today; the guard is what keeps that from being an
      // assumption the next reader has to verify.
      if (data && SDO_ID_PATTERN.test(record.id)) {
        rows.push({ id: record.id, data });
      }
    });
    offset += records.length;
    if (offset >= (response?.structuredDataObjects?.count ?? 0)) {
      break;
    }
  }
  return rows;
};

/**
 * Soft-deletes every live row for a TDO, then leaves the caller to write the
 * tombstone.
 *
 * Works from the RAW SDO `data`, never the mapped record: the write is an
 * upsert, so re-writing from a mapped row would silently drop any field this
 * client version does not know about — a row written by a newer writer would
 * come back stripped.
 *
 * Pages to the end first, because the modal may only have loaded the first
 * page and a partial clear is worse than none. Idempotent by construction: a
 * run that fails part-way leaves the tagged rows tagged, and running it again
 * picks up whatever is still live.
 *
 * Resolves the number of rows tagged, or throws — unlike a history WRITE this
 * is a user-initiated action with a button behind it, so the caller can react.
 */
const softDeleteStatusHistoryRows = async (
  tdoId: string,
  dispatch: GQLApiDispatch,
  getState: GQLApiGetState
): Promise<number> => {
  const schemaId = await resolveStatusHistoryGate()(dispatch, getState);
  if (!schemaId) {
    return 0;
  }
  const live = (
    await collectRowsForTdo(tdoId, schemaId, dispatch, getState)
  ).filter((row) => row.data['isDeleted'] !== true);
  for (let i = 0; i < live.length; i += BULK_ROW_CHUNK) {
    const chunk = live.slice(i, i + BULK_ROW_CHUNK);
    const variables: Record<string, unknown> = { schemaId };
    chunk.forEach((row, index) => {
      variables[`data${index}`] = { ...row.data, isDeleted: true };
    });
    const response = await callGraphQLApi<Record<string, unknown>>({
      actionTypes: [NOOP, NOOP, NOOP],
      query: buildSoftDeleteRowsQuery(chunk),
      variables,
      dispatch,
      getState,
      bailout: undefined,
    });
    if (!response) {
      throw new Error('status history clear declined');
    }
  }
  return live.length;
};

/**
 * The whole clear, as one thunk: tag every live row, then append the tombstone.
 *
 * A thunk rather than a saga sequence because both halves need `dispatch` and
 * `getState` and must be awaited in order — the tombstone is written LAST, only
 * once the tagging has succeeded, so a run that dies part-way never records a
 * clear that did not happen. Tagging is idempotent, so pressing Clear again
 * finishes the job.
 *
 * Dispatches the re-read itself: the tombstone needs its server-assigned
 * createdDateTime before the UI can date it, and the tagged rows have to come
 * back so the reducer replaces the stale copies it already holds.
 */
export const clearStatusHistory =
  (tdoId: string) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState): Promise<void> => {
    try {
      await softDeleteStatusHistoryRows(tdoId, dispatch, getState);
    } catch (err) {
      // Classified, never raw (SECURITY-03): the soft-delete submits whole
      // rows, so a validation error echoing the offending value would put note
      // text and an acting user's email into Sentry via console breadcrumbs.
      console.error('statusHistory: clear failed, nothing marked', {
        tdoId,
        kind: classifyErrorForLog(err),
      });
      dispatch(
        FETCH_STATUS_HISTORY_FAILURE({ tdoId, message: 'clear failed' })
      );
      return;
    }
    await writeStatusHistoryRow({
      tdoId,
      // The tombstone records no status — nothing transitioned.
      statusLabel: '',
      statusKey: '',
      isCustomStatus: false,
      isSystem: true,
      origin: 'cleared',
    })(dispatch, getState);
    // Reset BEFORE the refetch, and only on this path: the success handler
    // merges rather than replaces, so without it a history longer than one page
    // keeps stale un-tagged copies of everything past row 50 and goes on
    // rendering them. A failed clear never gets here, which is deliberate —
    // the list must survive intact so the error means something.
    dispatch(RESET_STATUS_HISTORY({ tdoId }));
    dispatch(FETCH_STATUS_HISTORY({ tdoId }));
  };

/**
 * Hard-deletes every status-history row for TDOs that have themselves been
 * permanently deleted (VE-26730).
 *
 * A real delete, not the `isDeleted` flag the Clear button writes: clearing
 * hides a trail that still belongs to a live file, whereas here the file is
 * gone and the rows have nothing left to describe. Leaving them would strand
 * the acting users' emails and the free text of their correction notes in the
 * registry indefinitely, referenced by nothing.
 *
 * Best-effort and never rejects. This runs on the tail of a delete the user has
 * already been told succeeded; failing loudly here would report a deletion
 * failure for a file that is, in fact, deleted. A failed pass leaves rows to be
 * swept another day, which is the lesser problem — but it is logged, because
 * silence about retained personal data is the wrong default.
 */
export const purgeStatusHistoryForTdos =
  (tdoIds: readonly string[]) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState): Promise<void> => {
    if (tdoIds.length === 0) {
      return;
    }
    try {
      const schemaId = await resolveStatusHistoryGate()(dispatch, getState);
      if (!schemaId) {
        // Never provisioned here: there is nothing of ours to clean up.
        return;
      }
      for (const tdoId of tdoIds) {
        const rows = await collectRowsForTdo(
          tdoId,
          schemaId,
          dispatch,
          getState
        );
        for (let i = 0; i < rows.length; i += BULK_ROW_CHUNK) {
          const ids = rows.slice(i, i + BULK_ROW_CHUNK).map((row) => row.id);
          await callGraphQLApi<Record<string, unknown>>({
            actionTypes: [NOOP, NOOP, NOOP],
            query: buildDeleteRowsQuery(ids),
            variables: { schemaId },
            dispatch,
            getState,
            bailout: undefined,
          });
        }
      }
    } catch (err) {
      console.error(
        'statusHistory: purge after TDO delete failed; rows retained',
        { tdoIds, kind: classifyErrorForLog(err) }
      );
    }
  };

const getActingUserEmail = (getState: GQLApiGetState): string => {
  const user = userModule.selectUser(getState()) as
    | { email?: string; userName?: string }
    | undefined;
  return user?.email || user?.userName || '';
};

/**
 * Writes one append-only history row. Best-effort: resolves void on success
 * AND on terminal failure (after one schema-cache-clear retry). On success it
 * refreshes the latest-row cache so the note icon/pop-up react to in-editor
 * transitions without a refetch.
 */
export const writeStatusHistoryRow =
  (input: RecordStatusChangeInput) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState): Promise<void> => {
    try {
      // A tombstone carries no status by design; everything else must.
      if (!input.tdoId || (!input.statusKey && input.origin !== 'cleared')) {
        return;
      }
      const schemaId = await resolveStatusHistoryGate()(dispatch, getState);
      if (!schemaId) {
        // 'disabled' is the normal unprovisioned state — stay silent (US-9).
        // Anything else means a real transition happened while the schema
        // could not be resolved (lookup failure / cooldown): that row is LOST,
        // and the loss must be visible somewhere (NFR N-10).
        if (selectStatusHistoryGate(getState()) !== 'disabled') {
          reportRowLoss(dispatch, input, 'gate_unresolved');
        }
        return;
      }
      const data = mapStatusChangeToSdoData(
        input,
        getActingUserEmail(getState)
      );

      // One id for the row, NOT one per attempt: the retry below re-sends the
      // same forced id, so a write that landed server-side but lost its
      // response can't become a second row for one transition.
      const rowId = uuid();

      const writeOnce = async (targetSchemaId: string) => {
        const response = await callGraphQLApi<WriteStatusHistoryRowResponse>({
          actionTypes: [NOOP, NOOP, NOOP],
          query: writeStatusHistoryRowQuery,
          variables: { id: rowId, schemaId: targetSchemaId, data },
          dispatch,
          getState,
          bailout: undefined,
        });
        // callGraphQLApi resolves undefined (rather than throwing) for a
        // not_found error — for this mutation that means the schema id is gone
        // (deleted or republished). A declined write must NEVER read as
        // success; word it as a schema error so isStaleSchemaError routes it
        // through the cache-clear retry below.
        if (!response?.createStructuredData) {
          throw new Error('write declined: schema not found or no record');
        }
        return response;
      };

      await enqueueHistoryPersist(async () => {
        let response: WriteStatusHistoryRowResponse | undefined;
        try {
          response = await writeOnce(schemaId);
        } catch (err) {
          // A republished schema invalidates the cached id; clear the cache,
          // re-resolve once, retry once (pattern: redactionStatuses). Scoped to
          // schema/validation errors on purpose — retrying a transport failure
          // by discarding a known-good schema id is how a single blip used to
          // take the whole feature down for the session.
          if (!isStaleSchemaError(err)) {
            throw err;
          }
          clearCachedSchemaId(getSchemaCacheKey(getState));
          const freshSchemaId = await resolveStatusHistoryGate()(
            dispatch,
            getState
          );
          if (!freshSchemaId) {
            throw err;
          }
          response = await writeOnce(freshSchemaId);
        }
        const record = mapSdoRecordToStatusHistoryRecord(
          response.createStructuredData
        );
        if (!record) {
          // The write reported success but echoed no usable record — surface
          // it rather than silently skipping the latest-row refresh.
          throw new Error('write response missing record fields');
        }
        dispatch(
          FETCH_LATEST_STATUS_ROW_SUCCESS({ tdoId: input.tdoId, record })
        );
      });
    } catch (err) {
      // BR-2: never propagate — report and move on. Fixed strings only (see
      // classifyErrorForLog): no note content and no server-authored message.
      reportRowLoss(dispatch, input, classifyErrorForLog(err));
    }
  };

/** Tier 1: the newest row for a TDO (limit 1). */
export const fetchLatestStatusRow =
  ({ tdoId }: { tdoId: string }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState): Promise<void> => {
    try {
      const schemaId = await resolveStatusHistoryGate()(dispatch, getState);
      if (!schemaId) {
        return;
      }
      const response = await callGraphQLApi<FetchStatusHistoryResponse>({
        actionTypes: [NOOP, NOOP, NOOP],
        query: fetchStatusHistoryQuery,
        variables: { schemaId, tdoId, limit: 1, offset: 0 },
        dispatch,
        getState,
        bailout: undefined,
      });
      if (!response?.structuredDataObjects) {
        // undefined = callGraphQLApi swallowed a not_found (stale/deleted
        // schema). An empty history is records: [] — this is an error, and
        // rendering it as "no history" would hide real rows.
        throw new Error('read declined: schema not found');
      }
      const record = mapSdoRecordToStatusHistoryRecord(
        response.structuredDataObjects.records?.[0]
      );
      dispatch(FETCH_LATEST_STATUS_ROW_SUCCESS({ tdoId, record }));
    } catch (err) {
      console.error('statusHistory: failed to fetch latest row', {
        tdoId,
        kind: classifyErrorForLog(err),
      });
      dispatch(
        FETCH_LATEST_STATUS_ROW_FAILURE({
          tdoId,
          message: err instanceof Error ? err.message : 'fetch failed',
        })
      );
    }
  };

/** Tier 2: one page of the full history (table, on demand). */
export const fetchStatusHistoryPage =
  ({ tdoId, offset = 0 }: { tdoId: string; offset?: number }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState): Promise<void> => {
    try {
      const schemaId = await resolveStatusHistoryGate()(dispatch, getState);
      if (!schemaId) {
        return;
      }
      const response = await callGraphQLApi<FetchStatusHistoryResponse>({
        actionTypes: [NOOP, NOOP, NOOP],
        query: fetchStatusHistoryQuery,
        variables: {
          schemaId,
          tdoId,
          limit: STATUS_HISTORY_PAGE_SIZE,
          offset,
        },
        dispatch,
        getState,
        bailout: undefined,
      });
      if (!response?.structuredDataObjects) {
        // See fetchLatestStatusRow — a swallowed not_found must not render as
        // an empty page (which would also hide "Load more").
        throw new Error('read declined: schema not found');
      }
      const records = (response.structuredDataObjects.records ?? [])
        .map(mapSdoRecordToStatusHistoryRecord)
        .filter((r): r is StatusHistoryRecord => r !== null);
      dispatch(
        FETCH_STATUS_HISTORY_SUCCESS({
          tdoId,
          records,
          count: response.structuredDataObjects.count ?? records.length,
        })
      );
    } catch (err) {
      console.error('statusHistory: failed to fetch history page', {
        tdoId,
        offset,
        kind: classifyErrorForLog(err),
      });
      dispatch(
        FETCH_STATUS_HISTORY_FAILURE({
          tdoId,
          message: err instanceof Error ? err.message : 'fetch failed',
        })
      );
    }
  };
